import { inject } from 'inversify';

import { service } from '@/decorators/service';
import { SignalrListener } from '@/decorators/signalr';
import { LayoutStore } from '@/stores/LayoutStore';
import { ILayout } from '~/ILayout';
import { ITable } from '~/ITable';
import { IWindow } from '~/IWindow';

@SignalrListener
@service(LayoutManager)
export class LayoutManager {
  @inject(LayoutStore) protected layoutStore: LayoutStore;

  get getLayouts() {
    return this.layoutStore.all();
  }

  get getDefaultLayout() {
    const layouts = this.getLayouts;
    return layouts.find((layout) => layout.default === true) || null;
  }

  get getActiveLayout() {
    const layouts = this.getLayouts;
    return layouts.find((layout) => layout.active === true) || null;
  }

  updateLayouts(layouts: ILayout[]) {
    this.layoutStore.update(...layouts);
  }

  deleteLayout(layoutId: string) {
    this.layoutStore.remove(layoutId);
  }

  updateLayout(layout: ILayout) {
    this.layoutStore.update(layout);
  }

  setActiveLayout(layoutId: string) {
    const layouts = this.getLayouts;
    const updatedLayouts = layouts.map((layout) => ({
      ...layout,
      active: layout.id === layoutId,
    }));

    this.updateLayouts(updatedLayouts);
  }

  setDefaultLayout(layoutId: string) {
    const layout = this.findLayout(layoutId);
    if (!layout || !layout.id) return;
    this.updateLayout({ ...layout, default: true });
  }

  findLayout(id: string) {
    return this.layoutStore.find(id);
  }

  findLayoutByName(name: string) {
    return this.getLayouts.find((layout) => layout.name.toLowerCase() === name);
  }

  updateWindows(windows: IWindow[], layoutId: string) {
    const layout = this.findLayout(layoutId);
    if (!layout || !layout.id) return;
    this.layoutStore.update({ ...layout, windows });
  }

  updateWindow(window: IWindow, layoutId: string) {
    const layout = this.findLayout(layoutId);
    if (!layout || !layout.id) return;
    this.layoutStore.update({ ...layout, windows: layout.windows.map((w) => (w.id === window.id ? window : w)) });
  }

  updateLayoutTable(table: ITable, layoutId: string) {
    const layout = this.findLayout(layoutId);
    if (!layout || !layout.id) return;
    const tableIndex = layout.tables?.findIndex((t) => t.id === table.id);
    if (!tableIndex || tableIndex === -1) {
      // Table not found: add it to existing ones
      const updatedTables = layout.tables ? [...layout.tables, table] : [table];
      this.layoutStore.update({ ...layout, tables: updatedTables });
    }
    else {
      this.layoutStore.update({ ...layout, tables: layout.tables?.map((t) => (t.id === table.id ? table : t)) });
    }
  }

  findActiveLayoutTable(tableId: string) {
    const layout = this.getActiveLayout;
    if (!layout || !layout.id || !layout.tables) return null;
    return layout.tables.find((table) => table.id === tableId) || null;
  }
}
